// The schedule before the season opens (PLAN-2026-10-29-the-schedule-before-the-season
// §4, scenario B4): every regular week and who plays whom, as the season
// would open now, and for a commissioner a way to draw it again.
//
// SAID BEFORE IT IS PRESSED (CLAUDE.md, say what will happen): a draw cannot
// be taken back, as Yahoo's shuffle cannot, so the sentence beside the button
// says so and the button asks once more before it acts (`Confirm`).

import { useState } from 'react';
import type { TeamRef, UpcomingSchedule, UpcomingScheduleWeek } from '@fsp/domain';
import { TeamMark } from '../../kit';
import { Panel } from '../../kit/Panel';
import { Caption, Chip, Text, tell } from '../../kit/ui';
import { Confirm } from '../shared/Confirm';

export function UpcomingSeason({
  upcoming,
  myTeamId,
  onDraw,
}: {
  upcoming: UpcomingSchedule;
  myTeamId: string;
  /** Draws it again; the answer says whether it was. */
  onDraw: () => Promise<{ ok: true } | { ok: false; message: string }>;
}) {
  const [drawing, setDrawing] = useState(false);
  const teams = new Map(upcoming.teams.map((t) => [t.id, t]));
  const draw = async () => {
    setDrawing(true);
    const out = await onDraw();
    setDrawing(false);
    if (out.ok) tell('The schedule is drawn again.', 'good');
    else tell(out.message, 'bad');
  };

  return (
    <div className="fsp-up">
      <Panel title="Before the season" bodyClass="fsp-up-note">
        <Text size="sm" block>
          This is the schedule as it stands. It is set when the season opens, after the draft; until
          then a change to the teams, their divisions, the rivals or the rules changes it.
        </Text>
        {upcoming.canDraw ? (
          <div className="fsp-up-draw">
            <Text size="sm" tone="secondary" block>
              Drawing again shuffles who plays whom and when. The schedule before it cannot be put
              back, and the league’s chat is told.
            </Text>
            <Confirm
              label="Draw again"
              confirmLabel="Draw it again?"
              tone="warn"
              busy={drawing}
              onConfirm={() => void draw()}
            />
          </div>
        ) : null}
        {upcoming.problems.map((p) => (
          <Text key={p} size="sm" tone="secondary" block>
            {p}
          </Text>
        ))}
      </Panel>
      {upcoming.weeks.length === 0 ? (
        <div className="fsp-empty">No weeks to show yet.</div>
      ) : (
        <ol className="fsp-up-weeks">
          {upcoming.weeks.map((w) => (
            <Week key={w.period} week={w} teams={teams} myTeamId={myTeamId} />
          ))}
        </ol>
      )}
    </div>
  );
}

function Week({
  week,
  teams,
  myTeamId,
}: {
  week: UpcomingScheduleWeek;
  teams: ReadonlyMap<string, TeamRef>;
  myTeamId: string;
}) {
  // MY GAME FIRST, then the rest in the order they were drawn; a week of two
  // games (Q6, B-614) its first game's, then its second's, each so.
  const mine = (g: UpcomingScheduleWeek['games'][number]) =>
    g.homeTeamId === myTeamId || g.awayTeamId === myTeamId;
  const two = week.games.some((g) => g.secondGame);
  const parts = (two ? [false, true] : [false]).map((second) => {
    const these = week.games.filter((g) => g.secondGame === second);
    return {
      second,
      games: [...these.filter(mine), ...these.filter((g) => !mine(g))],
    };
  });
  const byes = week.byes.map((id) => teams.get(id)?.name ?? id);
  return (
    <li className="fsp-up-week">
      <div className="fsp-up-week-head">
        <span className="fsp-up-week-label">{week.label ?? `Week ${week.period}`}</span>
        {week.rivalry ? <Chip tone="bad">Rivalry</Chip> : null}
      </div>
      {parts.map((part) => (
        <div key={String(part.second)}>
          {two ? <Caption>{part.second ? 'Second game' : 'First game'}</Caption> : null}
          {two ? <Resting teams={teams} games={part.games} /> : null}
          <ul className="fsp-up-games">
            {part.games.map((g) => {
              const home = teams.get(g.homeTeamId);
              const away = g.awayTeamId === null ? undefined : teams.get(g.awayTeamId);
              return (
                <li
                  key={`${g.homeTeamId}-${g.awayTeamId ?? 'field'}`}
                  className={`fsp-up-game${mine(g) ? ' fsp-up-mine' : ''}`}
                >
                  <Side team={home} />
                  <span className="fsp-up-vs fsp-muted">
                    {g.awayTeamId === null ? 'plays the league' : 'vs'}
                  </span>
                  {g.awayTeamId === null ? <span /> : <Side team={away} />}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      {byes.length > 0 ? (
        <Text size="sm" tone="secondary" block>
          Bye: {byes.join(', ')}
        </Text>
      ) : null}
    </li>
  );
}

/** The teams a game of a week of two rests (Q6): an odd-sized league rests one a game. */
function Resting({
  teams,
  games,
}: {
  teams: ReadonlyMap<string, TeamRef>;
  games: readonly UpcomingScheduleWeek['games'][number][];
}) {
  const playing = new Set(games.flatMap((g) => [g.homeTeamId, g.awayTeamId]));
  const resting = [...teams.values()].filter((t) => !playing.has(t.id)).map((t) => t.name);
  if (resting.length === 0) return null;
  return (
    <Text size="sm" tone="secondary" block>
      Sits out: {resting.join(', ')}
    </Text>
  );
}

function Side({ team }: { team: TeamRef | undefined }) {
  if (team === undefined) return <span className="fsp-up-side fsp-muted">A team</span>;
  return (
    <span className="fsp-up-side">
      <TeamMark team={team} size={18} />
      <span className="fsp-up-name">{team.name}</span>
    </span>
  );
}
